import React, { useState, useRef, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import sendMessageMutation from '../../graphql/mutations/sendMessage.mutation';
import SendIcon from '@mui/icons-material/Send';
import { useCurrentUserProfile } from '../../utils';
import VerifyEmailDialog from '../VerifyEmailDialog';
import './PrivateMessages.css';

const QUICK_REPLIES = [
  'Is this still available?',
  "What's your best price?",
  'Can I schedule a viewing?',
  'Any accidents or mechanical issues?',
];

interface MessageInputProps {
  conversationId: number;
  onMessageSent?: (content: string) => void;
  darkMode?: boolean;
  showQuickReplies?: boolean;
}

const MessageInput: React.FC<MessageInputProps> = ({
  conversationId,
  onMessageSent,
  darkMode = false,
  showQuickReplies = false,
}) => {
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { emailVerified, profile, refetch } = useCurrentUserProfile();
  const [verifyOpen, setVerifyOpen] = useState(false);
  const isUnverified = emailVerified === false;
  const token = typeof window !== 'undefined' ? window.localStorage.getItem('token') : null;

  const [sendMessage, { loading }] = useMutation(sendMessageMutation, {
    onCompleted: (data) => {
      const content = data?.sendMessage?.privateMessage?.content || '';
      setMessage('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
      onMessageSent?.(content);
    },
    onError: (error) => {
      console.error('Error sending message:', error);
      if (/email/i.test(error.message) && /verif/i.test(error.message)) {
        setVerifyOpen(true);
        return;
      }
      alert(error.message || 'Failed to send message. Please try again.');
    },
  });

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [message]);

  const submitContent = async (content: string) => {
    const trimmed = content.trim();
    if (!trimmed || loading) return;
    if (isUnverified) {
      setVerifyOpen(true);
      return;
    }
    await sendMessage({
      variables: {
        conversationId,
        content: trimmed,
      },
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitContent(message);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <form className={`message-input-form ${darkMode ? 'dark' : ''}`} onSubmit={handleSubmit}>
      {isUnverified && (
        <p className="message-input-verify-hint">
          Verify your email to message sellers.{' '}
          <button
            type="button"
            className="message-input-verify-link"
            onClick={() => setVerifyOpen(true)}
          >
            Verify now
          </button>
        </p>
      )}
      {showQuickReplies && !isUnverified && (
        <div className="message-quick-replies" data-testid="message-quick-replies">
          {QUICK_REPLIES.map((reply) => (
            <button
              key={reply}
              type="button"
              className="message-quick-reply"
              disabled={loading}
              onClick={() => submitContent(reply)}
            >
              {reply}
            </button>
          ))}
        </div>
      )}
      <div className="message-input-wrapper">
        <textarea
          ref={textareaRef}
          data-testid="message-input"
          placeholder={isUnverified ? 'Verify your email to send messages' : 'Ask about the car, price, or a viewing…'}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading || isUnverified}
          rows={1}
          maxLength={4000}
          className="message-textarea"
          aria-label="Message"
        />
        <button
          type="submit"
          data-testid="message-send"
          disabled={!message.trim() || loading || isUnverified}
          className="send-message-btn"
          aria-label="Send message"
        >
          <SendIcon />
        </button>
      </div>
      {token && (
        <VerifyEmailDialog
          open={verifyOpen}
          token={token}
          email={profile?.email}
          darkMode={darkMode}
          onVerified={() => {
            setVerifyOpen(false);
            refetch();
          }}
          onClose={() => setVerifyOpen(false)}
        />
      )}
    </form>
  );
};

export default MessageInput;
